import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2, Plus, Trash2, ChevronDown, ChevronRight, X, Settings, Wallet, Save, UserRound, Users, Check, Search, Pencil,
} from "lucide-react";

export default function Assignments() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projectSearch, setProjectSearch] = useState("");
  // Team management (inline grid) state
  const [bulkProject, setBulkProject] = useState(null);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkHours, setBulkHours] = useState(100);
  const [bulkContract, setBulkContract] = useState("");
  const [bulkStart, setBulkStart] = useState("");
  const [bulkEnd, setBulkEnd] = useState("");
  const [bulkPermanent, setBulkPermanent] = useState(false);
  const [bulkSearch, setBulkSearch] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  // Financial editor state
  const [finProject, setFinProject] = useState(null);
  const [finForm, setFinForm] = useState({ budget_amount: 0, budget_hours: 0, direct_expenses: 0, indirect_expenses_pct: 0 });
  const [finBusy, setFinBusy] = useState(false);

  // New project state
  const [newProject, setNewProject] = useState(false);
  const [projBusy, setProjBusy] = useState(false);
  const emptyProj = { name: "", code: "", saga_code: "", client_name: "", location: "", sector: "", description: "", status: "planning", start_date: "", end_date: "", budget_amount: 0, budget_hours: 0 };
  const [projForm, setProjForm] = useState(emptyProj);
  // Team allocation at project creation
  const [projTeamMode, setProjTeamMode] = useState("none"); // none | all | some
  const [projTeamSelected, setProjTeamSelected] = useState(new Set());
  const [projTeamHours, setProjTeamHours] = useState(100);
  const [projTeamStart, setProjTeamStart] = useState("");
  const [projTeamEnd, setProjTeamEnd] = useState("");
  const [projTeamPermanent, setProjTeamPermanent] = useState(false);

  // Edit project state
  const [editProject, setEditProject] = useState(null);
  const [editForm, setEditForm] = useState(emptyProj);
  const [editBusy, setEditBusy] = useState(false);

  const canManageProjects = hasPermission("projects.manage");

  const asgByProject = useMemo(() => {
    const m = {};
    for (const a of assignments) (m[a.project_id] ||= []).push(a);
    return m;
  }, [assignments]);

  async function load() {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        api.get("/projects"),
        api.get("/staff/assignments"),
      ]);
      setProjects(p.data);
      setAssignments(a.data);
      if (hasPermission("users.manage") || hasPermission("assignments.manage") || hasPermission("projects.manage")) {
        try { const u = await api.get("/staff/assignable-users"); setEmployees(u.data); }
        catch (_) { /* not authorized — ok */ }
      }
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function refreshAssignments() {
    // reîncarcă doar alocările, fără loader global (nu clipește tot tab-ul)
    try { const a = await api.get("/staff/assignments"); setAssignments(a.data); }
    catch (e) { toast.error(apiErr(e)); }
  }

  async function removeProject(p) {
    if (!window.confirm(`Sigur ștergi proiectul „${p.name}"? Se vor șterge și alocările și jaloanele asociate. Acțiunea este ireversibilă.`)) return;
    try {
      await api.delete(`/staff/projects/${p.id}`);
      toast.success("Proiect șters");
      if (expanded === p.id) setExpanded(null);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
  }

  async function createProject() {
    if (!projForm.name.trim() || !projForm.code.trim()) return toast.error("Completează numele și codul proiectului");
    if (projTeamMode === "some" && projTeamSelected.size === 0)
      return toast.error("Selectează cel puțin o persoană sau schimbă modul de alocare");
    setProjBusy(true);
    try {
      const res = await api.post("/staff/projects", {
        name: projForm.name.trim(),
        code: projForm.code.trim(),
        saga_code: projForm.saga_code || "",
        client_name: projForm.client_name || "",
        location: projForm.location,
        sector: projForm.sector,
        description: projForm.description,
        status: projForm.status,
        start_date: projForm.start_date || null,
        end_date: projForm.end_date || null,
        budget_amount: parseFloat(projForm.budget_amount) || 0,
        budget_hours: parseFloat(projForm.budget_hours) || 0,
      });
      const newId = res.data?.id;
      let allocMsg = "";
      if (newId && projTeamMode !== "none") {
        try {
          const r = await api.post("/staff/assignments/bulk", {
            project_id: newId,
            employee_ids: projTeamMode === "some" ? [...projTeamSelected] : [],
            all_employees: projTeamMode === "all",
            allocated_hours: parseFloat(projTeamHours) || 0,
            start_date: projTeamPermanent ? null : (projTeamStart || null),
            end_date: projTeamPermanent ? null : (projTeamEnd || null),
            permanent: projTeamPermanent,
          });
          const c = r.data?.created || 0;
          allocMsg = c ? ` · ${c} ${c === 1 ? "angajat alocat" : "angajați alocați"}` : "";
        } catch (e) { toast.error(apiErr(e)); }
      }
      toast.success(`Proiect creat${allocMsg}`);
      setNewProject(false);
      setProjForm(emptyProj);
      setProjTeamMode("none");
      setProjTeamSelected(new Set());
      setProjTeamHours(100);
      setProjTeamStart("");
      setProjTeamEnd("");
      setProjTeamPermanent(false);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setProjBusy(false); }
  }

  function openBulk(project) {
    setBulkProject((cur) => (cur && cur.id === project.id ? null : project));
    setBulkSelected(new Set());
    setBulkHours(100);
    setBulkContract("");
    setBulkStart("");
    setBulkEnd("");
    setBulkPermanent(false);
    setBulkSearch("");
  }

  function toggleBulk(id) {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function bulkAvailable() {
    if (!bulkProject) return [];
    const assignedIds = new Set(
      assignments.filter((a) => a.project_id === bulkProject.id).map((a) => a.employee_id)
    );
    const s = bulkSearch.trim().toLowerCase();
    return employees.filter((e) =>
      e.is_active !== false && !assignedIds.has(e.id) &&
      (!s || (e.name || "").toLowerCase().includes(s) || (e.job_title || "").toLowerCase().includes(s))
    );
  }

  function toggleBulkAll() {
    const avail = bulkAvailable();
    setBulkSelected((prev) =>
      prev.size === avail.length && avail.length > 0
        ? new Set()
        : new Set(avail.map((e) => e.id))
    );
  }

  async function submitBulk() {
    if (bulkSelected.size === 0) return toast.error("Selectează cel puțin un angajat");
    setBulkBusy(true);
    try {
      const r = await api.post("/staff/assignments/bulk", {
        project_id: bulkProject.id,
        employee_ids: [...bulkSelected],
        all_employees: false,
        allocated_hours: parseFloat(bulkHours) || 0,
        contract_hours_month: bulkContract ? parseFloat(bulkContract) : null,
        start_date: bulkPermanent ? null : (bulkStart || null),
        end_date: bulkPermanent ? null : (bulkEnd || null),
        permanent: bulkPermanent,
      });
      const { created, skipped } = r.data;
      const noun = created === 1 ? "angajat alocat" : "angajați alocați";
      toast.success(`${created} ${noun}${skipped ? `, ${skipped} deja alocați` : ""}`);
      setBulkProject(null);
      await refreshAssignments();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBulkBusy(false); }
  }

  async function updateAssignment(id, patch) {
    // actualizare optimistă locală — fără reîncărcarea întregului tab
    setAssignments((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
    try {
      const { data } = await api.put(`/staff/assignments/${id}`, patch);
      if (data && data.id) setAssignments((prev) => prev.map((a) => (a.id === data.id ? data : a)));
    } catch (e) {
      toast.error(apiErr(e));
      await load(); // revenire la starea reală în caz de eroare
    }
  }

  async function removeAssignment(id) {
    if (!window.confirm("Sigur dezaloci acest angajat?")) return;
    const prev = assignments;
    setAssignments((list) => list.filter((a) => a.id !== id)); // scoatere optimistă
    try { await api.delete(`/staff/assignments/${id}`); toast.success("Angajat dezalocat"); }
    catch (e) { toast.error(apiErr(e)); setAssignments(prev); }
  }

  async function setProjectManager(projectId, pmId) {
    try {
      await api.put(`/staff/projects/${projectId}/manager`, { project_manager_id: pmId || null });
      toast.success("Project Manager actualizat");
      await load();
    } catch (e) { toast.error(apiErr(e)); }
  }

  function openEdit(p) {
    setEditForm({
      name: p.name || "", code: p.code || "", saga_code: p.saga_code || "", client_name: p.client_name || "",
      location: p.location || "", sector: p.sector || "", description: p.description || "",
      status: p.status || "planning", start_date: p.start_date || "", end_date: p.end_date || "",
      budget_amount: p.budget_amount || 0, budget_hours: p.budget_hours || 0,
    });
    setEditProject(p);
  }

  async function saveEdit() {
    if (!editForm.name.trim() || !editForm.code.trim()) return toast.error("Completează numele și codul proiectului");
    setEditBusy(true);
    try {
      await api.put(`/staff/projects/${editProject.id}`, {
        name: editForm.name.trim(),
        code: editForm.code.trim(),
        saga_code: editForm.saga_code || "",
        client_name: editForm.client_name || "",
        location: editForm.location,
        sector: editForm.sector,
        description: editForm.description,
        status: editForm.status,
        start_date: editForm.start_date || null,
        end_date: editForm.end_date || null,
        budget_amount: parseFloat(editForm.budget_amount) || 0,
        budget_hours: parseFloat(editForm.budget_hours) || 0,
      });
      toast.success("Proiect actualizat");
      setEditProject(null);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setEditBusy(false); }
  }

  function openFinancials(p) {
    setFinProject(p);
    setFinForm({
      budget_amount: p.budget_amount || 0,
      budget_hours: p.budget_hours || 0,
      direct_expenses: p.direct_expenses || 0,
      indirect_expenses_pct: p.indirect_expenses_pct || 0,
    });
  }

  async function saveFinancials() {
    setFinBusy(true);
    try {
      await api.put(`/staff/projects/${finProject.id}/financials`, {
        budget_amount: parseFloat(finForm.budget_amount) || 0,
        budget_hours: parseFloat(finForm.budget_hours) || 0,
        direct_expenses: parseFloat(finForm.direct_expenses) || 0,
        indirect_expenses_pct: parseFloat(finForm.indirect_expenses_pct) || 0,
      });
      toast.success("Bugetul proiectului actualizat");
      setFinProject(null);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setFinBusy(false); }
  }

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1400px]">
        <div className="mb-10">
          <div className="text-[13px] font-display font-medium text-ink-muted mb-3">— Alocări echipă</div>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-display font-semibold text-[42px] md:text-[48px] tracking-[-0.02em] leading-tight text-ink">Echipe pe proiect</h1>
              <p className="mt-3 text-ink-muted">Alocă angajați, setează bugete de ore și configurează finanțele fiecărui proiect.</p>
            </div>
            {canManageProjects && (
              <button data-testid="project-new" onClick={() => { setProjForm(emptyProj); setProjTeamMode("none"); setProjTeamSelected(new Set()); setProjTeamHours(100); setNewProject(true); }}
                className="btn-brand py-2.5 px-5 text-sm shrink-0">
                <Plus className="h-4 w-4" /> Proiect nou
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-ink-muted">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Se încarcă...
          </div>
        ) : (
          <>
            <div className="mb-4 flex items-center gap-2 max-w-md h-11 px-3 rounded-lg border border-neutral-200 bg-white focus-within:border-brand">
              <Search className="h-4 w-4 text-neutral-400 shrink-0" />
              <input
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
                placeholder="Caută proiect după nume, cod sau cod SAGA..."
                data-testid="project-search"
                style={{ boxShadow: "none" }}
                className="flex-1 bg-transparent outline-none border-0 text-sm placeholder:text-neutral-400" />
              {projectSearch && (
                <button type="button" data-testid="project-search-clear"
                  onClick={() => setProjectSearch("")}
                  className="text-ink-muted hover:text-ink shrink-0"><X className="h-4 w-4" /></button>
              )}
            </div>
            <div className="space-y-3" data-testid="assignments-list">
            {(() => {
              const q = projectSearch.trim().toLowerCase();
              const visible = q
                ? projects.filter((p) =>
                    (p.name || "").toLowerCase().includes(q) ||
                    (p.code || "").toLowerCase().includes(q) ||
                    (p.saga_code || "").toLowerCase().includes(q))
                : projects;
              if (visible.length === 0) {
                return <div className="py-16 text-center text-sm text-ink-muted">Niciun proiect nu corespunde căutării.</div>;
              }
              return visible.map((p) => {
              const rows = asgByProject[p.id] || [];
              const isOpen = expanded === p.id;
              const allocatedTotal = rows.reduce((s, r) => s + (r.allocated_hours || 0), 0);
              return (
                <div key={p.id} className="bg-white border border-neutral-200 rounded-2xl md:rounded-none overflow-hidden">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 px-4 md:px-6 py-4 hover:bg-ink-soft transition-colors">
                    <button onClick={() => setExpanded(isOpen ? null : p.id)}
                      className="flex items-center gap-3 md:gap-4 text-left flex-1 min-w-0"
                      data-testid={`assign-project-${p.code}`}>
                      {isOpen ? <ChevronDown className="h-4 w-4 text-brand shrink-0" /> : <ChevronRight className="h-4 w-4 text-ink-muted shrink-0" />}
                      <div className="min-w-0">
                        <div className="font-mono text-[11px] text-ink-muted">{p.code}</div>
                        <h3 className="font-display font-bold text-lg tracking-tight">{p.name}</h3>
                      </div>
                    </button>
                    <div className="flex items-center gap-3 md:gap-6 text-sm flex-wrap pl-7 md:pl-0">
                      <div className="md:text-right">
                        <div className="text-ink-muted text-[11px] font-mono uppercase tracking-widest">Echipă</div>
                        <div className="font-display font-bold">{rows.length}</div>
                      </div>
                      <div className="md:text-right">
                        <div className="text-ink-muted text-[11px] font-mono uppercase tracking-widest">Alocat</div>
                        <div className="font-display font-bold text-brand">{allocatedTotal.toFixed(0)} %</div>
                      </div>
                      <div className="md:text-right">
                        <div className="text-ink-muted text-[11px] font-mono uppercase tracking-widest">Buget</div>
                        <div className="font-display font-bold">{p.budget_hours || "—"} h</div>
                      </div>
                      <div className="flex items-center gap-2 md:gap-6 flex-wrap basis-full md:basis-auto">
                      {hasPermission("client.manage") && (
                        <button
                          data-testid={`client-portal-${p.code}`}
                          onClick={() => navigate(`/staff/client/${p.id}`)}
                          title="Configurează portalul clientului pentru acest proiect"
                          className="flex items-center gap-1.5 px-3 py-2 border border-neutral-200 hover:border-brand hover:text-brand text-xs transition-colors"
                        ><UserRound className="h-3.5 w-3.5" /> Portal client{(p.client_user_ids || []).length ? ` (${p.client_user_ids.length})` : ""}</button>
                      )}
                      {canManageProjects && (
                        <button
                          data-testid={`project-edit-${p.code}`}
                          onClick={() => openEdit(p)}
                          className="flex items-center gap-1.5 px-3 py-2 border border-neutral-200 hover:border-brand hover:text-brand text-xs transition-colors"
                        ><Pencil className="h-3.5 w-3.5" /> Editează</button>
                      )}
                      {canManageProjects && (
                        <button
                          data-testid={`fin-open-${p.code}`}
                          onClick={() => openFinancials(p)}
                          className="flex items-center gap-1.5 px-3 py-2 border border-neutral-200 hover:border-brand hover:text-brand text-xs transition-colors"
                        ><Wallet className="h-3.5 w-3.5" /> Finanțe</button>
                      )}
                      {canManageProjects && (
                        <button
                          data-testid={`project-delete-${p.code}`}
                          onClick={() => removeProject(p)}
                          className="flex items-center gap-1.5 px-3 py-2 border border-neutral-200 hover:border-red-500 hover:text-red-600 text-xs transition-colors"
                        ><Trash2 className="h-3.5 w-3.5" /> Șterge</button>
                      )}
                      </div>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="border-t border-neutral-100 p-6">
                      {/* Project Manager selector */}
                      <div className="mb-5 p-4 rounded-xl bg-brand/[0.04] border border-brand/15 flex items-center gap-4 flex-wrap">
                        <div className="text-[13px] font-display font-semibold text-ink">
                          Project Manager
                        </div>
                        <select
                          value={p.project_manager_id || ""}
                          onChange={(e) => setProjectManager(p.id, e.target.value)}
                          disabled={!canManageProjects}
                          data-testid={`pm-select-${p.code}`}
                          className="min-w-[260px] px-3 py-2 rounded-lg border border-neutral-200 bg-white text-sm focus:border-brand focus:outline-none disabled:opacity-60"
                        >
                          <option value="">— fără PM desemnat —</option>
                          {rows.map((a) => (
                            <option key={a.employee_id} value={a.employee_id}>
                              {a.employee?.name} ({a.employee?.job_title || a.role_on_project})
                            </option>
                          ))}
                        </select>
                        <div className="text-[12px] text-ink-muted flex-1 min-w-[220px]">
                          Cererile de acces la acest proiect vor fi aprobate de PM după Managerul Superior.
                        </div>
                      </div>

                      {rows.length > 0 && (
                        <table className="pio-table pio-table-static mb-4">
                          <thead>
                            <tr>
                              <th>Angajat</th>
                              <th>Rol</th>
                              <th>Perioadă alocare</th>
                              <th>Alocat (%)</th>
                              <th>Alocare contractuală (h/lună)</th>
                              <th>Fee rate (€)</th>
                              <th>Fee rate override (€)</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((a) => (
                              <tr key={a.id}>
                                <td className="py-2.5">
                                  <div className="font-medium">{a.employee?.name}</div>
                                  <div className="text-xs text-ink-muted">{a.employee?.job_title}</div>
                                </td>
                                <td className="py-2.5">
                                  <div className="text-sm text-ink">{a.employee?.job_title || "—"}</div>
                                  <div className="text-[11px] text-ink-muted">preluat din profilul angajatului</div>
                                </td>
                                <td className="py-2.5">
                                  <div className="flex items-center gap-1.5">
                                    <input type="date" key={`s-${a.id}-${a.permanent ? 1 : 0}`} defaultValue={a.start_date || ""}
                                      data-testid={`asg-start-${a.id}`} disabled={!!a.permanent}
                                      onBlur={(e) => (e.target.value || null) !== (a.start_date || null) && updateAssignment(a.id, { start_date: e.target.value || null })}
                                      className="px-2 py-1 border border-neutral-200 focus:border-brand focus:outline-none text-[12px] disabled:opacity-40 disabled:bg-neutral-50" />
                                    <span className="text-ink-muted text-xs">→</span>
                                    <input type="date" key={`e-${a.id}-${a.permanent ? 1 : 0}`} defaultValue={a.end_date || ""}
                                      data-testid={`asg-end-${a.id}`} disabled={!!a.permanent}
                                      onBlur={(e) => (e.target.value || null) !== (a.end_date || null) && updateAssignment(a.id, { end_date: e.target.value || null })}
                                      className="px-2 py-1 border border-neutral-200 focus:border-brand focus:outline-none text-[12px] disabled:opacity-40 disabled:bg-neutral-50" />
                                  </div>
                                  <label className="flex items-center gap-1.5 mt-1 text-[11px] text-ink-muted cursor-pointer w-fit">
                                    <input type="checkbox" checked={!!a.permanent}
                                      data-testid={`asg-permanent-${a.id}`}
                                      onChange={(e) => updateAssignment(a.id, { permanent: e.target.checked })} />
                                    Alocare permanentă
                                  </label>
                                </td>
                                <td className="py-2.5">
                                  <div className="flex items-center gap-1">
                                    <input type="number" step="5" min="0" max="100" defaultValue={a.allocated_hours}
                                      data-testid={`asg-hours-${a.id}`}
                                      onBlur={(e) => parseFloat(e.target.value) !== a.allocated_hours && updateAssignment(a.id, { allocated_hours: parseFloat(e.target.value) || 0 })}
                                      className="w-20 px-2 py-1 border border-neutral-200 focus:border-brand focus:outline-none text-sm font-mono" />
                                    <span className="text-ink-muted text-sm">%</span>
                                  </div>
                                </td>
                                <td className="py-2.5">
                                  <input type="number" step="8" placeholder="—"
                                    data-testid={`asg-contract-${a.id}`}
                                    defaultValue={a.contract_hours_month ?? ""}
                                    onBlur={(e) => {
                                      const v = e.target.value ? parseFloat(e.target.value) : null;
                                      if (v !== (a.contract_hours_month ?? null)) updateAssignment(a.id, { contract_hours_month: v });
                                    }}
                                    className="w-24 px-2 py-1 border border-neutral-200 focus:border-brand focus:outline-none text-sm font-mono" />
                                </td>
                                <td className="py-2.5">
                                  <div className="font-mono text-sm" data-testid={`asg-fee-${a.id}`}
                                    title="Preluat automat din fișa angajatului — Tarif orar (EUR/oră)">
                                    {a.employee?.hourly_fee_rate != null ? Number(a.employee.hourly_fee_rate).toFixed(2) : "—"}
                                  </div>
                                  <div className="text-[10px] text-ink-muted">din fișa angajatului</div>
                                </td>
                                <td className="py-2.5">
                                  <input type="number" step="5" placeholder="—"
                                    data-testid={`asg-override-${a.id}`}
                                    defaultValue={a.hourly_rate_override ?? ""}
                                    onBlur={(e) => {
                                      const v = e.target.value ? parseFloat(e.target.value) : null;
                                      if (v !== (a.hourly_rate_override ?? null)) updateAssignment(a.id, { hourly_rate_override: v });
                                    }}
                                    className="w-24 px-2 py-1 border border-neutral-200 focus:border-brand focus:outline-none text-sm font-mono" />
                                </td>
                                <td className="py-2.5 text-right">
                                  <button data-testid={`unassign-${a.id}`} onClick={() => removeAssignment(a.id)} className="text-red-600 hover:text-red-800">
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}

                      {/* Team management — inline, readable, no dropdown */}
                      <div className="mt-2">
                        <button
                          data-testid={`assign-manage-${p.code}`}
                          onClick={() => openBulk(p)}
                          className={`flex items-center gap-2 text-sm font-medium transition-colors ${bulkProject?.id === p.id ? "text-ink" : "text-brand hover:text-brand-hover"}`}
                        >
                          <Users className="h-4 w-4" />
                          {bulkProject?.id === p.id ? "Ascunde lista angajaților" : "Adaugă membri în echipă"}
                          <ChevronDown className={`h-4 w-4 transition-transform ${bulkProject?.id === p.id ? "rotate-180" : ""}`} />
                        </button>

                        {bulkProject?.id === p.id && (() => {
                          const avail = bulkAvailable();
                          const allSelected = avail.length > 0 && avail.every((e) => bulkSelected.has(e.id));
                          return (
                            <div className="mt-3 border border-neutral-200 rounded-xl overflow-hidden" data-testid={`team-grid-${p.code}`}>
                              <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-100 bg-neutral-50/60 flex-wrap">
                                <div className="flex items-center gap-2 flex-1 min-w-[220px] h-9 px-3 rounded-lg border border-neutral-200 bg-white">
                                  <Search className="h-4 w-4 text-neutral-400" />
                                  <input
                                    value={bulkSearch}
                                    onChange={(e) => setBulkSearch(e.target.value)}
                                    placeholder="Caută angajat după nume sau funcție..."
                                    data-testid="team-search"
                                    style={{ boxShadow: "none" }}
                                    className="flex-1 bg-transparent outline-none border-0 text-sm placeholder:text-neutral-400" />
                                </div>
                                <button
                                  type="button"
                                  data-testid="team-select-all"
                                  onClick={toggleBulkAll}
                                  disabled={avail.length === 0}
                                  className="flex items-center gap-2 text-sm text-brand hover:text-brand-hover font-medium disabled:opacity-40">
                                  <span className={`h-4 w-4 rounded border flex items-center justify-center ${allSelected ? "bg-brand border-brand" : "border-neutral-300"}`}>
                                    {allSelected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                  </span>
                                  {allSelected ? "Deselectează toți" : "Selectează toți"}
                                </button>
                              </div>

                              {avail.length === 0 ? (
                                <div className="py-8 text-center text-sm text-ink-muted">
                                  {bulkSearch.trim() ? "Niciun angajat nu corespunde căutării." : "Toți angajații activi sunt deja în echipă."}
                                </div>
                              ) : (
                                <div className="max-h-[300px] overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-px bg-neutral-100">
                                  {avail.map((e) => {
                                    const on = bulkSelected.has(e.id);
                                    return (
                                      <button
                                        key={e.id}
                                        type="button"
                                        onClick={() => toggleBulk(e.id)}
                                        data-testid={`team-emp-${e.id}`}
                                        className={`text-left flex items-center gap-3 px-4 py-3 bg-white transition-colors ${on ? "bg-brand/[0.05]" : "hover:bg-ink-soft"}`}>
                                        <span className={`shrink-0 h-5 w-5 rounded border flex items-center justify-center ${on ? "bg-brand border-brand" : "border-neutral-300"}`}>
                                          {on && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                                        </span>
                                        <span className="h-8 w-8 rounded-full bg-neutral-100 text-ink-muted flex items-center justify-center text-[11px] font-semibold shrink-0">
                                          {(e.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                                        </span>
                                        <span className="min-w-0">
                                          <span className="block text-sm font-medium text-ink truncate">{e.name}</span>
                                          <span className="block text-[12px] text-ink-muted truncate">{e.job_title || e.role}</span>
                                        </span>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}

                              <div className="flex items-center gap-3 px-4 py-3 border-t border-neutral-100 bg-neutral-50/60 flex-wrap">
                                <div className="flex items-center gap-2">
                                  <label className="text-[12px] text-ink-muted">Alocat (%) / persoană</label>
                                  <input
                                    type="number" step="5" min="0" max="100" value={bulkHours}
                                    onChange={(e) => setBulkHours(e.target.value)}
                                    data-testid="team-hours"
                                    className="w-24 px-2 py-1.5 rounded-lg border border-neutral-200 focus:border-brand focus:outline-none text-sm font-mono" />
                                </div>
                                <div className="flex items-center gap-2">
                                  <label className="text-[12px] text-ink-muted">Alocare contractuală (h/lună)</label>
                                  <input
                                    type="number" step="8" value={bulkContract} placeholder="—"
                                    onChange={(e) => setBulkContract(e.target.value)}
                                    data-testid="team-contract-hours"
                                    className="w-24 px-2 py-1.5 rounded-lg border border-neutral-200 focus:border-brand focus:outline-none text-sm font-mono" />
                                </div>
                                <div className="flex items-center gap-2">
                                  <label className="text-[12px] text-ink-muted">Perioadă</label>
                                  <input type="date" value={bulkStart} onChange={(e) => setBulkStart(e.target.value)}
                                    data-testid="team-start" disabled={bulkPermanent}
                                    className="px-2 py-1.5 rounded-lg border border-neutral-200 focus:border-brand focus:outline-none text-[12px] disabled:opacity-40 disabled:bg-neutral-50" />
                                  <span className="text-ink-muted text-xs">→</span>
                                  <input type="date" value={bulkEnd} onChange={(e) => setBulkEnd(e.target.value)}
                                    data-testid="team-end" disabled={bulkPermanent}
                                    className="px-2 py-1.5 rounded-lg border border-neutral-200 focus:border-brand focus:outline-none text-[12px] disabled:opacity-40 disabled:bg-neutral-50" />
                                </div>
                                <label className="flex items-center gap-1.5 text-[12px] text-ink-muted cursor-pointer">
                                  <input type="checkbox" checked={bulkPermanent}
                                    data-testid="team-permanent"
                                    onChange={(e) => setBulkPermanent(e.target.checked)} />
                                  Alocare permanentă
                                </label>
                                <div className="text-[12px] text-ink-muted">
                                  {bulkSelected.size} selectați · rolul se preia din funcția fiecăruia
                                </div>
                                <button
                                  data-testid="team-confirm"
                                  onClick={submitBulk}
                                  disabled={bulkBusy || bulkSelected.size === 0}
                                  className="ml-auto btn-brand py-2 px-4 text-sm disabled:opacity-50">
                                  {bulkBusy ? <><Loader2 className="h-4 w-4 animate-spin" /> Se alocă</> : <><Plus className="h-4 w-4" /> Alocă {bulkSelected.size || ""}</>}
                                </button>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              );
            });
            })()}
            </div>
          </>
        )}
      </div>

      {/* New Project Modal */}
      <Dialog open={newProject} onOpenChange={(o) => !o && setNewProject(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl flex items-center gap-2">
              <Plus className="h-6 w-6 text-brand" /> Proiect nou
            </DialogTitle>
            <DialogDescription>
              Creează un proiect nou. Poți aloca echipa și configura bugetul după creare.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <ProjField label="Nume proiect *" value={projForm.name} onChange={(v) => setProjForm({ ...projForm, name: v })} testid="proj-name" />
            <ProjField label="Cod proiect *" value={projForm.code} onChange={(v) => setProjForm({ ...projForm, code: v })} testid="proj-code" placeholder="BRP-2026-001" />
            <ProjField label="Cod SAGA" value={projForm.saga_code} onChange={(v) => setProjForm({ ...projForm, saga_code: v })} testid="proj-saga" placeholder="Cod contabil SAGA (doar în export)" />
            <ProjField label="Client" value={projForm.client_name} onChange={(v) => setProjForm({ ...projForm, client_name: v })} testid="proj-client" placeholder="Nume client" />
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Status</label>
              <select data-testid="proj-status" value={projForm.status}
                onChange={(e) => setProjForm({ ...projForm, status: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 border border-neutral-200 bg-white focus:border-brand focus:outline-none text-sm">
                <option value="planning">Planificare</option>
                <option value="active">Activ</option>
                <option value="completed">Finalizat</option>
              </select>
            </div>
            <ProjField label="Locație" value={projForm.location} onChange={(v) => setProjForm({ ...projForm, location: v })} testid="proj-location" />
            <ProjField label="Sector" value={projForm.sector} onChange={(v) => setProjForm({ ...projForm, sector: v })} testid="proj-sector" placeholder="Rezidențial, Office..." />
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Data început</label>
              <input data-testid="proj-start" type="date" value={projForm.start_date}
                onChange={(e) => setProjForm({ ...projForm, start_date: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 border border-neutral-200 focus:border-brand focus:outline-none text-sm" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Data sfârșit</label>
              <input data-testid="proj-end" type="date" value={projForm.end_date}
                onChange={(e) => setProjForm({ ...projForm, end_date: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 border border-neutral-200 focus:border-brand focus:outline-none text-sm" />
            </div>
            <ProjField label="Buget total (€)" type="number" value={projForm.budget_amount} onChange={(v) => setProjForm({ ...projForm, budget_amount: v })} testid="proj-budget" />
            <ProjField label="Buget ore (h)" type="number" value={projForm.budget_hours} onChange={(v) => setProjForm({ ...projForm, budget_hours: v })} testid="proj-hours" />
            <div className="col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Descriere</label>
              <textarea data-testid="proj-desc" rows={3} value={projForm.description}
                onChange={(e) => setProjForm({ ...projForm, description: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 border border-neutral-200 focus:border-brand focus:outline-none text-sm" />
            </div>
          </div>

          {/* Team allocation */}
          <div className="mt-5 border-t border-neutral-100 pt-5">
            <div className="flex items-center gap-2 mb-3">
              <Users className="h-4 w-4 text-brand" />
              <div className="font-display font-semibold text-sm text-ink">Alocă echipa</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                { k: "none", t: "Nu aloca acum" },
                { k: "all", t: "Toți angajații" },
                { k: "some", t: "Anumite persoane" },
              ].map((o) => (
                <button
                  key={o.k}
                  type="button"
                  data-testid={`proj-team-mode-${o.k}`}
                  onClick={() => setProjTeamMode(o.k)}
                  className={`px-3 py-2.5 border text-sm transition-colors ${projTeamMode === o.k ? "border-brand bg-brand/[0.05] text-brand font-medium" : "border-neutral-200 hover:border-neutral-400"}`}
                >{o.t}</button>
              ))}
            </div>

            {projTeamMode !== "none" && (
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <label className="text-[12px] text-ink-muted">Alocat (%) / persoană</label>
                  <input
                    type="number" step="5" min="0" max="100" value={projTeamHours}
                    onChange={(e) => setProjTeamHours(e.target.value)}
                    data-testid="proj-team-hours"
                    className="w-24 px-2 py-1.5 border border-neutral-200 focus:border-brand focus:outline-none text-sm font-mono" />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-[12px] text-ink-muted">Perioadă</label>
                  <input type="date" value={projTeamStart} onChange={(e) => setProjTeamStart(e.target.value)}
                    data-testid="proj-team-start" disabled={projTeamPermanent}
                    className="px-2 py-1.5 border border-neutral-200 focus:border-brand focus:outline-none text-[12px] disabled:opacity-40 disabled:bg-neutral-50" />
                  <span className="text-ink-muted text-xs">→</span>
                  <input type="date" value={projTeamEnd} onChange={(e) => setProjTeamEnd(e.target.value)}
                    data-testid="proj-team-end" disabled={projTeamPermanent}
                    className="px-2 py-1.5 border border-neutral-200 focus:border-brand focus:outline-none text-[12px] disabled:opacity-40 disabled:bg-neutral-50" />
                </div>
                <label className="flex items-center gap-1.5 text-[12px] text-ink-muted cursor-pointer">
                  <input type="checkbox" checked={projTeamPermanent}
                    data-testid="proj-team-permanent"
                    onChange={(e) => setProjTeamPermanent(e.target.checked)} />
                  Alocare permanentă
                </label>
              </div>
            )}

            {projTeamMode === "all" && (
              <div className="mt-2 text-[12px] text-ink-muted">
                Toți angajații activi vor fi alocați pe proiect. Rolul se preia automat din funcția fiecăruia.
              </div>
            )}

            {projTeamMode === "some" && (
              <div className="mt-3">
                {employees.filter(e => e.is_active !== false).length === 0 ? (
                  <div className="text-[12px] text-ink-muted">Nu ai acces la lista de angajați.</div>
                ) : (
                  <>
                    <button
                      type="button"
                      data-testid="proj-team-select-all"
                      onClick={() => {
                        const avail = employees.filter(e => e.is_active !== false);
                        setProjTeamSelected(prev => prev.size === avail.length && avail.length > 0 ? new Set() : new Set(avail.map(e => e.id)));
                      }}
                      className="mb-2 flex items-center gap-2 text-sm text-brand hover:text-brand-hover font-medium"
                    >
                      <span className={`h-4 w-4 border flex items-center justify-center ${projTeamSelected.size === employees.filter(e => e.is_active !== false).length && projTeamSelected.size > 0 ? "bg-brand border-brand" : "border-neutral-300"}`}>
                        {projTeamSelected.size === employees.filter(e => e.is_active !== false).length && projTeamSelected.size > 0 && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                      </span>
                      Selectează toți ({projTeamSelected.size} aleși)
                    </button>
                    <div className="max-h-[220px] overflow-y-auto border border-neutral-200 divide-y divide-neutral-100">
                      {employees.filter(e => e.is_active !== false).map((e) => {
                        const on = projTeamSelected.has(e.id);
                        return (
                          <button
                            key={e.id}
                            type="button"
                            data-testid={`proj-team-emp-${e.id}`}
                            onClick={() => setProjTeamSelected(prev => {
                              const n = new Set(prev);
                              if (n.has(e.id)) n.delete(e.id); else n.add(e.id);
                              return n;
                            })}
                            className={`w-full text-left flex items-center gap-3 px-3 py-2.5 transition-colors ${on ? "bg-brand/[0.04]" : "hover:bg-ink-soft"}`}
                          >
                            <span className={`shrink-0 h-4 w-4 border flex items-center justify-center ${on ? "bg-brand border-brand" : "border-neutral-300"}`}>
                              {on && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                            </span>
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-ink truncate">{e.name}</div>
                              <div className="text-[12px] text-ink-muted truncate">{e.job_title || e.role}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button onClick={() => setNewProject(false)} className="px-4 py-2.5 border border-neutral-200 hover:border-ink text-sm">Renunță</button>
            <button data-testid="proj-save" onClick={createProject} disabled={projBusy} className="btn-brand disabled:opacity-60">
              {projBusy ? <><Loader2 className="h-4 w-4 animate-spin" /> Se creează</> : <><Plus className="h-4 w-4" /> Creează proiect</>}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Financials Modal */}
      <Dialog open={!!finProject} onOpenChange={(o) => !o && setFinProject(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl flex items-center gap-2">
              <Wallet className="h-6 w-6 text-brand" /> Setări finanțe proiect
            </DialogTitle>
            <DialogDescription>
              {finProject?.code} · {finProject?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <FinField label="Buget total (€)" value={finForm.budget_amount} onChange={(v) => setFinForm({ ...finForm, budget_amount: v })} testid="fin-budget" />
            <FinField label="Buget ore (h)" value={finForm.budget_hours} onChange={(v) => setFinForm({ ...finForm, budget_hours: v })} testid="fin-hours" />
            <FinField label="Cheltuieli directe (€)" value={finForm.direct_expenses} onChange={(v) => setFinForm({ ...finForm, direct_expenses: v })} testid="fin-direct" />
            <FinField label="Cheltuieli indirecte (%)" value={finForm.indirect_expenses_pct} onChange={(v) => setFinForm({ ...finForm, indirect_expenses_pct: v })} testid="fin-indirect" />
          </div>
          <div className="mt-3 p-3 bg-ink-soft text-xs text-ink-muted">
            Cheltuielile indirecte se aplică ca procent peste costul intern din pontaje. Sunt folosite pentru calculul marjei reale.
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <button onClick={() => setFinProject(null)} className="px-4 py-2.5 border border-neutral-200 hover:border-ink text-sm">Renunță</button>
            <button data-testid="fin-save" onClick={saveFinancials} disabled={finBusy} className="btn-brand disabled:opacity-60">
              {finBusy ? <><Loader2 className="h-4 w-4 animate-spin" /> Se salvează</> : <><Save className="h-4 w-4" /> Salvează</>}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Project Modal */}
      <Dialog open={!!editProject} onOpenChange={(o) => !o && setEditProject(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl flex items-center gap-2">
              <Pencil className="h-6 w-6 text-brand" /> Editează proiect
            </DialogTitle>
            <DialogDescription>
              {editProject?.code} · Modifică detaliile proiectului.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <ProjField label="Nume proiect *" value={editForm.name} onChange={(v) => setEditForm({ ...editForm, name: v })} testid="edit-proj-name" />
            <ProjField label="Cod proiect *" value={editForm.code} onChange={(v) => setEditForm({ ...editForm, code: v })} testid="edit-proj-code" placeholder="BRP-2026-001" />
            <ProjField label="Cod SAGA" value={editForm.saga_code} onChange={(v) => setEditForm({ ...editForm, saga_code: v })} testid="edit-proj-saga" placeholder="Cod contabil SAGA (doar în export)" />
            <ProjField label="Client" value={editForm.client_name} onChange={(v) => setEditForm({ ...editForm, client_name: v })} testid="edit-proj-client" placeholder="Nume client" />
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Status</label>
              <select data-testid="edit-proj-status" value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 border border-neutral-200 bg-white focus:border-brand focus:outline-none text-sm">
                <option value="planning">Planificare</option>
                <option value="active">Activ</option>
                <option value="completed">Finalizat</option>
              </select>
            </div>
            <ProjField label="Locație" value={editForm.location} onChange={(v) => setEditForm({ ...editForm, location: v })} testid="edit-proj-location" />
            <ProjField label="Sector" value={editForm.sector} onChange={(v) => setEditForm({ ...editForm, sector: v })} testid="edit-proj-sector" placeholder="Rezidențial, Office..." />
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Data început</label>
              <input data-testid="edit-proj-start" type="date" value={editForm.start_date}
                onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 border border-neutral-200 focus:border-brand focus:outline-none text-sm" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Data sfârșit</label>
              <input data-testid="edit-proj-end" type="date" value={editForm.end_date}
                onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 border border-neutral-200 focus:border-brand focus:outline-none text-sm" />
            </div>
            <ProjField label="Buget total (€)" type="number" value={editForm.budget_amount} onChange={(v) => setEditForm({ ...editForm, budget_amount: v })} testid="edit-proj-budget" />
            <ProjField label="Buget ore (h)" type="number" value={editForm.budget_hours} onChange={(v) => setEditForm({ ...editForm, budget_hours: v })} testid="edit-proj-hours" />
            <div className="col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Descriere</label>
              <textarea data-testid="edit-proj-desc" rows={3} value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 border border-neutral-200 focus:border-brand focus:outline-none text-sm" />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <button onClick={() => setEditProject(null)} className="px-4 py-2.5 border border-neutral-200 hover:border-ink text-sm">Renunță</button>
            <button data-testid="edit-proj-save" onClick={saveEdit} disabled={editBusy} className="btn-brand disabled:opacity-60">
              {editBusy ? <><Loader2 className="h-4 w-4 animate-spin" /> Se salvează</> : <><Save className="h-4 w-4" /> Salvează</>}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </PortalLayout>
  );
}

function FinField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{label}</label>
      <input
        data-testid={testid}
        type="number" step="any"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2.5 border border-neutral-200 focus:border-brand focus:outline-none font-mono"
      />
    </div>
  );
}

function ProjField({ label, value, onChange, testid, type = "text", placeholder = "" }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{label}</label>
      <input
        data-testid={testid}
        type={type}
        step={type === "number" ? "any" : undefined}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full px-3 py-2.5 border border-neutral-200 focus:border-brand focus:outline-none text-sm ${type === "number" ? "font-mono" : ""}`}
      />
    </div>
  );
}
